import { TranslateService } from '@ngx-translate/core';

/**
 * Etiqueta del rol de mantenimiento, en el idioma de la aplicación.
 *
 * El rol llega de distintas fuentes y en distintas formas: el enum del backend
 * (`TECHNICIAN`), la etiqueta que ya venía traducida a español (`Técnico`) o
 * alguna variante sin acento (`TECNICO`). Antes cada pantalla tenía su propio
 * mapa escrito a mano —cinco copias— con el texto en español fijo, así que la
 * columna Rol salía en español por más que la aplicación estuviera en otro
 * idioma. Acá se normaliza a la clave del enum y esa clave se traduce, que es
 * lo que ya hace el menú lateral.
 */
const CLAVES_POR_VARIANTE: Record<string, string> = {
  ADMINISTRATOR: 'ADMINISTRATOR',
  // Cuidado al leer esta línea: hoy "Administrador" es el nombre visible de
  // MANAGER, no de ADMINISTRATOR. Pero esta tabla no traduce etiquetas nuevas,
  // decodifica lo que quedó guardado: los registros viejos escribieron
  // "Administrador" cuando ese era el nombre de ADMINISTRATOR, y ahí tienen
  // que seguir apuntando. Lo que se guarda hoy es el enum, no la etiqueta.
  ADMINISTRADOR: 'ADMINISTRATOR',
  ADMIN: 'ADMINISTRATOR',
  SUPERADMIN: 'ADMINISTRATOR',
  // Los nombres nuevos, que no chocan con ningún registro viejo.
  PROPIETARIO: 'ADMINISTRATOR',
  OWNER: 'ADMINISTRATOR',
  MANAGER: 'MANAGER',
  GERENTE: 'MANAGER',
  COORDINATOR: 'COORDINATOR',
  COORDINADOR: 'COORDINATOR',
  COORDENADOR: 'COORDINATOR',
  GESTOR: 'COORDINATOR',
  TECHNICIAN: 'TECHNICIAN',
  TECNICO: 'TECHNICIAN',
  'TÉCNICO': 'TECHNICIAN',
  OPERATOR: 'OPERATOR',
  OPERADOR: 'OPERATOR',
  LEADER: 'LEADER',
  LIDER: 'LEADER',
  'LÍDER': 'LEADER',
  HELPER: 'HELPER',
  AYUDANTE: 'HELPER',
  AJUDANTE: 'HELPER',
};

/**
 * La clave del enum para un rol escrito de cualquier forma, o `null` si no se
 * reconoce. Va aparte para poder probar la normalización sin el traductor.
 */
export function claveDeRol(role?: string | null): string | null {
  const limpio = String(role ?? '').trim();
  if (!limpio) return null;
  return CLAVES_POR_VARIANTE[limpio.toUpperCase()] ?? null;
}

export function formatRole(role: string | null | undefined, translate: TranslateService): string {
  const clave = claveDeRol(role);
  if (clave) return translate.instant(clave);

  // Un rol que no está en la tabla se muestra tal cual vino: inventar
  // "Desconocido" esconde el dato en vez de mostrar que hay uno nuevo.
  const limpio = String(role ?? '').trim();
  return limpio || translate.instant('Sin rol');
}
